import { useMemo } from 'react';

/**
 * 관리자 권한 훅
 *
 * adminRole 종류:
 * - superadmin: 모든 권한 (모든 페이지, 모든 데이터 CRUD)
 * - teacher_all: 강사 관리자 - 모든 클래스 관리
 * - teacher_own: 강사 관리자 - 자기 클래스만 관리
 * - assistant: 담당 클래스 조회만 (읽기 전용)
 *
 * 하위호환: 기존 adminRole='teacher'는 teacher_own으로 취급
 */
const useAdminAuth = () => {
  const admin = useMemo(() => {
    try {
      const raw = localStorage.getItem('adminSession');
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }, []);

  const rawRole = admin?.adminRole || null;
  // 하위호환: 기존 'teacher' → 'teacher_own'으로 취급
  const adminRole = rawRole === 'teacher' ? 'teacher_own' : rawRole;
  const isTeacherRole = adminRole === 'teacher_all' || adminRole === 'teacher_own';

  return {
    // 관리자 정보
    admin,
    adminRole,
    isLoggedIn: !!admin,

    // 역할 체크
    isSuperAdmin: adminRole === 'superadmin',
    isTeacher: isTeacherRole,
    isTeacherAll: adminRole === 'teacher_all',
    isTeacherOwn: adminRole === 'teacher_own',
    isAssistant: adminRole === 'assistant',

    // 권한 헬퍼
    canCreate: adminRole === 'superadmin' || isTeacherRole,
    canEdit: adminRole === 'superadmin' || isTeacherRole,
    canDelete: adminRole === 'superadmin',
    canAccessSettings: adminRole === 'superadmin',
    canManageUsers: adminRole === 'superadmin',
    canSendMessage: adminRole === 'superadmin' || isTeacherRole,
    isReadOnly: adminRole === 'assistant',

    // 데이터 필터링: superadmin/teacher_all은 전체, teacher_own/assistant는 자기 것만
    filterByOwner: (items, ownerField = 'teacherId') => {
      if (adminRole === 'superadmin' || adminRole === 'teacher_all') return items;
      if (!admin?.uid) return [];
      return items.filter((item) => item[ownerField] === admin.uid);
    },

    // 특정 항목이 자기 것인지 체크
    isOwner: (item, ownerField = 'teacherId') => {
      if (adminRole === 'superadmin' || adminRole === 'teacher_all') return true;
      return item?.[ownerField] === admin?.uid;
    },
  };
};

export default useAdminAuth;
